import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Chip, 
  Divider, 
  Card, 
  CardContent, 
  Avatar, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon,
  Container,
  AppBar,
  Toolbar,
  CircularProgress,
  Link
} from '@mui/material';
import { 
  BusinessCenter, 
  School, 
  Star, 
  Phone, 
  Email, 
  LocationOn, 
  Code,
  Work,
  EmojiEvents,
  Assignment,
  GitHub,
  LinkedIn,
  Assessment
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { getCandidateResume } from '../../../services/resumeService';

const CandidateDetails = () => {
  const { jobId, resumeId } = useParams();
  const [resume, setResume] = useState(null);
  const [scores, setScores] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchResumeData = async () => {
      setLoading(true);
      try {
        const response = await getCandidateResume(jobId, resumeId);
        if (!response || !response.Resume) {
          throw new Error('Resume data not found');
        }

        setResume(response.Resume);
        setScores(response.Scores || null);
    
        if (response.Resume.candidate_info && response.Resume.candidate_info.Name) {
          document.title = `${response.Resume.candidate_info.Name} - Resume`;
        } else {
          document.title = 'Candidate Resume';
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch resume data');
      } finally {
        setLoading(false);
      }
    };

    if (jobId && resumeId) {
      fetchResumeData();
    } else {
      setError('Missing job ID or resume ID');
      setLoading(false);
    }
  }, [jobId, resumeId]);


  const getInitials = (name) => {
    if (!name) return '';
    
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`;
    }
    return name.charAt(0);
  };

  // parse JSON strings in the additional info and skills
  const parseJsonString = (jsonString) => {
    if (!jsonString) return null;
    
    try {
      if (typeof jsonString === 'string' && (jsonString.trim().startsWith('{') || jsonString.trim().startsWith('['))) {
        // Remove single quotes and replace with double quotes for valid JSON
        const fixedJsonString = jsonString.replace(/'/g, '"');
        return JSON.parse(fixedJsonString);
      }
      return jsonString; 
    } catch (error) {
      console.error('Error parsing JSON string:', error);
      return jsonString; 
    }
  };

  // extract projects from additional_info
  const extractProjects = (additionalInfo) => {
    if (!additionalInfo || !Array.isArray(additionalInfo) || additionalInfo.length === 0) return [];
    
    const projects = [];
    additionalInfo.forEach(info => {
      const parsed = parseJsonString(info);
      
      if (parsed && typeof parsed === 'object') {
        // Check if it contains a Projects array
        if (parsed.Projects && Array.isArray(parsed.Projects)) {
          projects.push(...parsed.Projects);
        }
      }
    });
    
    return projects;
  };

  // extract achievements from additional_info
  const extractAchievements = (additionalInfo) => {
    if (!additionalInfo || !Array.isArray(additionalInfo) || additionalInfo.length === 0) return [];
    
    let achievements = [];
    additionalInfo.forEach(info => {
      const parsed = parseJsonString(info);
      
      if (parsed && typeof parsed === 'object') {
        // Check if it contains an Achievements array
        if (parsed.Achievements && Array.isArray(parsed.Achievements)) {
          achievements = parsed.Achievements;
        }
      }
    });
    
    return achievements;
  };

  // Extract skills from the skill array
  const extractSkills = (skillArray) => {
    if (!skillArray || !Array.isArray(skillArray) || skillArray.length === 0) return [];
    
    const skillCategories = [];
    
    skillArray.forEach(skillItem => {
      const parsed = parseJsonString(skillItem);
      
      if (parsed && typeof parsed === 'object') {
        // Handle simple format
        Object.entries(parsed).forEach(([category, skills]) => {
          if (Array.isArray(skills)) {
            skillCategories.push({
              category: category,
              skills: skills
            });
          } 
          // Handle nested format
          else if (typeof skills === 'object') {
            Object.entries(skills).forEach(([subCategory, subSkills]) => {
              if (Array.isArray(subSkills)) {
                skillCategories.push({
                  category: `${category} - ${subCategory}`,
                  skills: subSkills
                });
              }
            });
          }
        });
      }
    });
    
    return skillCategories;
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
          <Typography variant="h5" sx={{ ml: 2 }}>Loading resume data...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Typography variant="h5" color="error">Error: {error}</Typography>
        </Box>
      </Container>
    );
  }

  if (!resume) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Typography variant="h5">No resume data available</Typography>
        </Box>
      </Container>
    );
  }

  const { candidate_info, education, work_experience, skill, additional_info } = resume;
  
  // Extract projects and achievements
  const projects = extractProjects(additional_info);
  const achievements = extractAchievements(additional_info);
  
  // Process skills
  const skillCategories = extractSkills(skill);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" color="default" elevation={0} sx={{ borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Candidate Resume
          </Typography>
        </Toolbar>
      </AppBar>
      
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          {/* Candidate Profile Card */}
          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Avatar 
                    sx={{ 
                      width: 120, 
                      height: 120, 
                      bgcolor: 'primary.main',
                      fontSize: '2.5rem',
                      mb: 2
                    }}
                  >
                    {getInitials(candidate_info?.Name || '')}
                  </Avatar>
                  <Typography variant="h5" align="center">
                    {candidate_info?.Name || 'No Name Available'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={9}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {candidate_info?.Title && (
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                          {candidate_info.Title}
                        </Typography>
                      )}
                      
                      {(candidate_info?.Email || (candidate_info?.Contact && candidate_info.Contact.Email)) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Email color="action" sx={{ mr: 1 }} />
                          <Typography variant="body1">
                            {candidate_info.Email || candidate_info.Contact.Email}
                          </Typography>
                        </Box>
                      )}
                    
                      {(candidate_info?.Phone || (candidate_info?.Contact && candidate_info.Contact.Phone)) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Phone color="action" sx={{ mr: 1 }} />
                          <Typography variant="body1">
                            {candidate_info.Phone || candidate_info.Contact.Phone}
                          </Typography>
                        </Box>
                      )}
                      
                      
                      {(candidate_info?.GitHub || (candidate_info?.Contact && candidate_info.Contact.GitHub)) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <GitHub color="action" sx={{ mr: 1 }} />
                          <Link 
                            href={
                              (candidate_info.GitHub || candidate_info.Contact.GitHub).startsWith('http') 
                                ? (candidate_info.GitHub || candidate_info.Contact.GitHub) 
                                : `https://github.com/${candidate_info.GitHub || candidate_info.Contact.GitHub}`
                            } 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            underline="hover"
                          >
                            {candidate_info.GitHub || candidate_info.Contact.GitHub}
                          </Link>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Scores Section */}
          {scores && (
            <Grid item xs={12}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <Assessment color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Scores
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={2}>
                    {[
                      { label: 'Resume Score', value: scores.resume_score },
                      { label: 'Interview Score', value: scores.interview_score },
                      { label: 'Screening Score', value: scores.screening_score },
                      { label: 'Overall Rank', value: scores.total_rank, isRank: true },
                    ].map((item, i) => (
                      <Grid item xs={6} sm={3} key={i}>
                        <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="h4" color={item.value != null ? 'primary' : 'text.disabled'}>
                            {item.value != null ? (item.isRank ? `#${item.value}` : Number(item.value).toFixed(1)) : 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {item.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {scores.screening_status && (
                    <Box sx={{ mt: 2 }}>
                      <Chip
                        label={`Screening: ${scores.screening_status}`}
                        color={
                          scores.screening_status === 'qualified' || scores.screening_status === 'intent-qualified'
                            ? 'success'
                            : scores.screening_status === 'disqualified'
                            ? 'error'
                            : 'default'
                        }
                        size="small"
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Education Section */}
          {education && Array.isArray(education) && education.length > 0 && (
            <Grid item xs={12}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <School color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Education
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  {education.map((edu, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {edu?.Duration || 'Duration not specified'}
                          </Typography>
                          {(edu?.GPA || edu?.CGPA || edu?.Percentage) && (
                            <Typography variant="body2" color="textSecondary">
                              {edu.GPA ? `GPA: ${edu.GPA}` : edu.CGPA ? `CGPA: ${edu.CGPA}` : `Percentage: ${edu.Percentage}`}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} sm={9}>
                          <Typography variant="h6">
                            {edu?.Degree || 'Degree not specified'}
                          </Typography>
                          <Typography variant="subtitle1" color="primary" gutterBottom>
                            {edu?.Institution || 'Institution not specified'}
                          </Typography>
                          {edu?.Major && (
                            <Typography variant="body2" color="textSecondary">
                              Major: {edu.Major}
                            </Typography>
                          )}
                          {edu?.Coursework && Array.isArray(edu.Coursework) && edu.Coursework.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="subtitle2">Relevant Courses:</Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                {edu.Coursework.map((course, i) => (
                                  <Chip 
                                    key={i}
                                    label={course} 
                                    variant="outlined"
                                    size="small"
                                    sx={{ m: 0.5 }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                      {index < education.length - 1 && <Divider sx={{ mt: 2, mb: 3 }} />}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Work Experience Section */}
          {work_experience && Array.isArray(work_experience) && work_experience.length > 0 && (
            <Grid item xs={12}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <Work color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Work Experience
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  {work_experience.map((exp, index) => (
                    <Box key={index} sx={{ mb: 4 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {exp?.Duration || 'Duration not specified'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={9}>
                          <Typography variant="h6">
                            {exp?.Job_Title || 'Position not specified'}
                          </Typography>
                          <Typography variant="subtitle1" gutterBottom color="primary">
                            {exp?.Company || 'Company not specified'}
                          </Typography>
                          
                          {exp?.Responsibilities && Array.isArray(exp.Responsibilities) && exp.Responsibilities.length > 0 && (
                            <>
                              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                                Responsibilities:
                              </Typography>
                              <List dense>
                                {exp.Responsibilities.map((responsibility, i) => (
                                  <ListItem key={i} sx={{ pl: 0 }}>
                                    <ListItemIcon sx={{ minWidth: '24px' }}>•</ListItemIcon>
                                    <ListItemText primary={responsibility} />
                                  </ListItem>
                                ))}
                              </List>
                            </>
                          )}
                        </Grid>
                      </Grid>
                      {index < work_experience.length - 1 && <Divider sx={{ mt: 2, mb: 3 }} />}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Skills Section */}
          {skillCategories && skillCategories.length > 0 && (
            <Grid item xs={12}>
              <Card elevation={3} sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <Code color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Skills
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  {skillCategories.map((category, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2">{category.category}</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {category.skills.map((skill, i) => (
                          <Chip 
                            key={i}
                            label={skill} 
                            variant="outlined"
                            color="primary"
                            sx={{ m: 0.5 }}
                          />
                        ))}
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Achievements Section */}
          {achievements && achievements.length > 0 && (
            <Grid item xs={12}>
              <Card elevation={3} sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <EmojiEvents color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Achievements
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <List dense>
                    {achievements.map((achievement, i) => (
                      <ListItem key={i} sx={{ pl: 0 }}>
                        <ListItemIcon sx={{ minWidth: '24px' }}>•</ListItemIcon>
                        <ListItemText primary={achievement} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Projects Section */}
          {projects && projects.length > 0 && (
            <Grid item xs={12}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <Assignment color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Projects
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  {projects.map((project, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                      <Typography variant="h6">{project.Name}</Typography>
                      
                      {project.Technologies && Array.isArray(project.Technologies) && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 1 }}>
                          {project.Technologies.map((tech, i) => (
                            <Chip 
                              key={i}
                              label={tech} 
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ m: 0.5 }}
                            />
                          ))}
                        </Box>
                      )}
                      
                      {project.Description && (
                        <Typography variant="body2" paragraph sx={{ mt: 1 }}>
                          {project.Description}
                        </Typography>
                      )}
                      
                      {index < projects.length - 1 && <Divider sx={{ mt: 1, mb: 2 }} />}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default CandidateDetails;